import React, { useState } from "react";

/**
 * Edit the operator-controlled fields on a server record. Replaces inline grid editing so that all
 * row operations live together in the Actions column and share the same dialog styling.
 *
 * Only fields the portal owns are editable here - everything else on the row is telemetry reported
 * by the server itself and would just be overwritten on the next heartbeat.
 */
export default function ServerEditDialog({ server, serverTypes = [], onSave, onClose }) {
  const [displayName, setDisplayName] = useState(server?.displayName || "");
  const [serverTypeId, setServerTypeId] = useState(
    server?.serverTypeId === null || server?.serverTypeId === undefined ? "" : String(server.serverTypeId)
  );
  const [busy, setBusy] = useState(false);

  const handleSave = async () => {
    setBusy(true);
    try {
      await onSave({
        displayName: displayName.trim(),
        serverTypeId: serverTypeId === "" ? null : parseInt(serverTypeId, 10),
      });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    padding: "6px 10px",
    borderRadius: "4px",
    border: "1px solid var(--border-color)",
    background: "var(--input-bg)",
    color: "var(--text-color)",
    width: "100%",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="portal-panel"
        style={{
          width: "480px", maxWidth: "92vw", padding: "20px",
          background: "var(--content-bg)", borderRadius: "var(--radius-md)",
        }}
      >
        <h2 style={{ margin: "0 0 4px 0", fontSize: "18px" }}>Edit Server</h2>
        <p style={{ margin: "0 0 18px 0", color: "var(--text-muted)", fontSize: "var(--fs-xs)" }}>
          {server?.serverId}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: 600 }}>Group name</label>
            <input
              className="toolbar-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={server?.groupName || "Reported group name"}
              style={inputStyle}
            />
            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
              Leave blank to use the name reported by the server ({server?.groupName || "unknown"}).
              Set an override when a restored backup still reports the original site's group.
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: 600 }}>Release channel</label>
            <select
              className="toolbar-select"
              value={serverTypeId}
              onChange={(e) => setServerTypeId(e.target.value)}
              style={inputStyle}
            >
              <option value="">-- Unassigned (Live/Global) --</option>
              {serverTypes.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name} ({st.sftpFolder})
                </option>
              ))}
            </select>
            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
              Determines which SFTP folder this server pulls updates from.
            </span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "22px" }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              padding: "8px 14px", borderRadius: "4px", cursor: "pointer",
              background: "transparent", color: "var(--text-color)",
              border: "1px solid var(--border-color)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={busy}
            style={{
              padding: "8px 14px", borderRadius: "4px", cursor: "pointer",
              background: "var(--brand-mid)", color: "white", border: "none",
            }}
          >
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
